<script setup lang="ts">
/**
 * Slack Manager — alert rules → Slack / Discord / Telegram / webhook / in-app.
 *
 * Imported from vmprod saas-monitoring.tools.oddscomposer.io/frontend/src/App.vue
 * (the "Alerts" view: Rules / Log / Buckets + create/edit modal), 2026-09-04 version.
 * Backend = saas-monitoring relay (server.ts on :3100), see src/config/slackManagerApi.js.
 *
 * Slack channels: a channel "name" that is a channel ID (C0…) is delivered through the
 * Slack Web API with SLACK_BOT_TOKEN on the relay; otherwise the channel "url" (incoming webhook)
 * is used. One rule can fan out to any number of channels.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import AlertLogRainChart from './AlertLogRainChart.vue'
import { resolveSlackManagerApiBase } from '../config/slackManagerApi.js'

const props = defineProps<{ isVisible: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

// Follows the console theme: themeStore toggles the `dark` class on <html> (tailwind darkMode: 'class')
const isDarkMode = ref(typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
let themeObserver: MutationObserver | null = null

// --------------- Shared formatting helpers (copied from saas-monitoring App.vue) ---------------
const getStatusColor = (s: number, d: boolean) => s >= 500 ? 'bg-red-500 text-white' : s >= 400 ? 'bg-yellow-500 text-black' : s >= 300 ? 'bg-blue-500 text-white' : d ? 'bg-green-700 text-green-100' : 'bg-green-500 text-white'
const getMethodColor = (m: string, d: boolean) => ({ GET: d?'text-green-400':'text-green-600', POST: d?'text-blue-400':'text-blue-600', PUT: d?'text-orange-400':'text-orange-600', PATCH: d?'text-yellow-400':'text-yellow-600', DELETE: d?'text-red-400':'text-red-600' } as Record<string, string>)[m] || (d?'text-gray-400':'text-gray-600')
const getDurationColor = (ms: number, d: boolean) => ms > 1000 ? (d?'text-red-400':'text-red-600') : ms > 500 ? (d?'text-yellow-400':'text-yellow-600') : (d?'text-green-400':'text-green-600')
const formatJson = (s: string | null | undefined) => { if (!s) return null; try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s } }
const formatBytes = (bytes: number | null | undefined) => {
  if (bytes === null || bytes === undefined) return '-'
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// --------------- Alerts UI State ---------------

type ComparisonOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'regex' | 'in' | 'not_in' | 'exists' | 'not_exists'
type EventType = 'api_access' | 'webhook' | 'flow' | 'system_stats' | 'any'

interface AlertCondition {
  field: string
  operator: ComparisonOp
  value: string
}

interface AlertChannel {
  type: 'slack' | 'discord' | 'webhook' | 'telegram' | 'in_app'
  url: string
  name: string
}

interface AlertRule {
  id: string
  name: string
  enabled: boolean
  eventType: EventType
  conditions: AlertCondition[]
  conditionLogic: 'and' | 'or'
  channels: AlertChannel[]
  cooldownMs: number
  sustainedMs: number
  /** Fire only after this many matching events inside windowMs (0/1 = every match). */
  windowCount?: number
  windowMs?: number
  /** In-app bell routing: critical (red) vs informational (neutral) */
  inAppSeverity?: 'critical' | 'informational'
  slackSnoozeUntil?: string
  createdAt: string
  updatedAt: string
  /** Server: times fired, in-app rows, successful external deliveries */
  fireStats?: { fires: number; inApp: number; externalOk: number }
}

interface AlertLogEntry {
  id: string
  ruleId: string
  ruleName: string
  firedAt: string
  eventSnapshot: Record<string, unknown>
  channelResults: { channel: string; success: boolean; error?: string }[]
}

const alertRules = ref<AlertRule[]>([])
const alertLog = ref<AlertLogEntry[]>([])
/** Global Slack snooze deadline (ISO) from server, or null */
const globalSlackSnoozeUntil = ref<string | null>(null)
const alertsLoading = ref(false)
const showGlobalSlackSnoozeMenu = ref(false)
const showRuleSlackSnoozeMenuId = ref<string | null>(null)
const showAlertForm = ref(false)
const editingAlert = ref<AlertRule | null>(null)
const alertsTab = ref<'rules' | 'log' | 'buckets'>('rules')
const testingAlertId = ref<string | null>(null)
const alertRulesFilter = ref('')
const alertLogFilter = ref('')
const alertLogSelectedEntry = ref<AlertLogEntry | null>(null)
const showLogMatchUnderlines = ref(true)
const alertLogViewTab = ref<'active' | 'archived'>('active')
/** Latest log entry id for rain-chart drop animation */
const rainDropAnimateId = ref<string | null>(null)
const archivedAlertIds = ref<Set<string>>(new Set(
  (() => {
    try {
      const s = localStorage.getItem('alert-log-archived-ids')
      if (s) {
        const arr = JSON.parse(s) as string[]
        return Array.isArray(arr) ? arr : []
      }
    } catch {}
    return []
  })()
))
const persistArchivedAlertIds = () => {
  localStorage.setItem('alert-log-archived-ids', JSON.stringify([...archivedAlertIds.value]))
}

/** Non-archived alert log rows for Criteria grid embed */
const criteriaGridAlertLogActive = computed(() =>
  alertLog.value.filter((e) => !archivedAlertIds.value.has(e.id)),
)
const archiveAlertEntry = (entry: AlertLogEntry) => {
  archivedAlertIds.value = new Set([...archivedAlertIds.value, entry.id])
  persistArchivedAlertIds()
  if (alertLogSelectedEntry.value?.id === entry.id) alertLogSelectedEntry.value = null
  fetch(`${alertApiBase.value}/archive-alert-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: entry.id }),
  }).catch(() => {})
}
const unarchiveAlertEntry = (entry: AlertLogEntry) => {
  const next = new Set(archivedAlertIds.value)
  next.delete(entry.id)
  archivedAlertIds.value = next
  persistArchivedAlertIds()
  if (alertLogSelectedEntry.value?.id === entry.id) alertLogSelectedEntry.value = null
}
const expandedAlertLogGroups = ref<Set<string>>(new Set())
const toggleLogGroup = (key: string) => {
  const s = new Set(expandedAlertLogGroups.value)
  if (s.has(key)) s.delete(key)
  else s.add(key)
  expandedAlertLogGroups.value = new Set(s)
}
watch(alertsTab, (tab) => {
  if (tab !== 'log' && tab !== 'buckets') alertLogSelectedEntry.value = null
})

const ALERT_LOG_COLUMNS = [
  { id: 'time', label: 'Time' },
  { id: 'rule', label: 'Rule' },
  { id: 'user_email', label: 'User' },
  { id: 'method', label: 'Method' },
  { id: 'status_code', label: 'Status' },
  { id: 'path', label: 'Path' },
  { id: 'route_action', label: 'Action' },
  { id: 'duration_ms', label: 'Duration' },
  { id: 'fixture_id', label: 'Fixture ID' },
  { id: 'player_id', label: 'Player ID' },
  { id: 'market', label: 'Market' },
  { id: 'ip', label: 'IP' },
  { id: 'bearer_token', label: 'Token' },
  { id: 'request_size', label: 'Req Size' },
  { id: 'response_size', label: 'Res Size' },
  { id: 'channels', label: 'Channels' },
] as const

type AlertLogColumnId = typeof ALERT_LOG_COLUMNS[number]['id']

const defaultAlertLogCols: Record<AlertLogColumnId, boolean> = {
  time: true, rule: true, user_email: true, method: true, status_code: true,
  path: true, route_action: true, duration_ms: true, fixture_id: false,
  player_id: false, market: false, ip: true, bearer_token: false,
  request_size: false, response_size: false, channels: true,
}
const alertLogVisibleCols = ref<Record<AlertLogColumnId, boolean>>({ ...defaultAlertLogCols })
const showAlertLogColsDropdown = ref(false)
const alertLogColsDropdownRef = ref<HTMLElement | null>(null)
const isAlertLogColVisible = (id: AlertLogColumnId) => alertLogVisibleCols.value[id] !== false
const toggleAlertLogCol = (id: AlertLogColumnId) => { alertLogVisibleCols.value = { ...alertLogVisibleCols.value, [id]: !alertLogVisibleCols.value[id] } }
const resetAlertLogCols = () => { alertLogVisibleCols.value = { ...defaultAlertLogCols } }

/** Map rule condition field names to log column IDs for underlining matched columns */
const CONDITION_FIELD_TO_COL: Record<string, AlertLogColumnId> = {
  status_code: 'status_code', method: 'method', path: 'path', full_url: 'path', endpoint_url: 'path',
  route_action: 'route_action', route_name: 'route_action', duration_ms: 'duration_ms',
  ip: 'ip', user_email: 'user_email', bearer_token: 'bearer_token', request_size: 'request_size',
  response_size: 'response_size', fixture_id: 'fixture_id', player_id: 'player_id', market: 'market',
}
const getLogEntryMatchedColumns = (entry: AlertLogEntry): Set<AlertLogColumnId> => {
  const rule = alertRules.value.find(r => r.id === entry.ruleId)
  if (!rule?.conditions?.length) return new Set()
  const cols = new Set<AlertLogColumnId>()
  for (const c of rule.conditions) {
    const colId = CONDITION_FIELD_TO_COL[c.field]
    if (colId) cols.add(colId)
  }
  return cols
}
const isLogColMatched = (entry: AlertLogEntry, colId: AlertLogColumnId) => getLogEntryMatchedColumns(entry).has(colId)
const shouldShowLogColUnderline = (entry: AlertLogEntry, colId: AlertLogColumnId) => showLogMatchUnderlines.value && isLogColMatched(entry, colId)
watch(alertLogVisibleCols, (val) => localStorage.setItem('alert-log-visible-columns', JSON.stringify(val)), { deep: true })

const OPERATOR_LABELS: Record<ComparisonOp, string> = {
  eq: '=', neq: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=',
  contains: 'contains', not_contains: 'not contains',
  starts_with: 'starts with', ends_with: 'ends with',
  regex: 'regex', in: 'in', not_in: 'not in',
  exists: 'exists', not_exists: 'not exists',
}

const API_FIELDS = [
  'status_code', 'method', 'path', 'full_url', 'route_action', 'route_name',
  'duration_ms', 'ip', 'user_email', 'user_id', 'user_agent', 'bearer_token',
  'has_query', 'request_size', 'response_size', 'payload_excerpt', 'response_excerpt',
  'command', 'event_type', 'endpoint_url', 'fixture_id',
]

const SYSTEM_STATS_FIELDS = [
  'cpu_percent', 'cpu_max_core_percent', 'num_cores',
  'mem_percent', 'mem_used_gb', 'mem_total_gb', 'mem_free_gb', 'mem_available_gb',
  'swap_percent', 'swap_used_gb', 'swap_total_gb',
]

const KNOWN_FIELDS = [...API_FIELDS, ...SYSTEM_STATS_FIELDS]

const CHANNEL_TYPES: { value: AlertChannel['type']; label: string }[] = [
  { value: 'in_app', label: 'In-App (this site)' },
  { value: 'slack', label: 'Slack' },
  { value: 'discord', label: 'Discord' },
  { value: 'webhook', label: 'Webhook (HTTP)' },
  { value: 'telegram', label: 'Telegram' },
]

const blankCondition = (eventType?: EventType): AlertCondition => {
  if (eventType === 'system_stats') return { field: 'cpu_percent', operator: 'gte', value: '90' }
  return { field: 'status_code', operator: 'gte', value: '500' }
}
const DEFAULT_SLACK_WEBHOOK = 'https://hooks.slack.com/services/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'
const blankChannel = (type: AlertChannel['type'] = 'in_app'): AlertChannel => ({
  type,
  url: type === 'slack' ? DEFAULT_SLACK_WEBHOOK : '',
  name: type === 'slack' ? '#saas-monitoring' : '',
})

const formData = ref({
  name: '',
  enabled: true,
  eventType: 'api_access' as EventType,
  conditionLogic: 'and' as 'and' | 'or',
  conditions: [blankCondition()] as AlertCondition[],
  channels: [blankChannel()] as AlertChannel[],
  cooldownMs: 0,
  sustainedMs: 0,
  windowCount: 0,
  windowMs: 0,
  inAppSeverity: 'critical' as 'critical' | 'informational',
})

const resetForm = () => {
  editingAlert.value = null
  formData.value = {
    name: '',
    enabled: true,
    eventType: 'api_access',
    conditionLogic: 'and',
    conditions: [blankCondition()],
    channels: [blankChannel()],
    cooldownMs: 0,
    sustainedMs: 0,
    windowCount: 0,
    windowMs: 0,
    inAppSeverity: 'critical',
  }
}

const openNewAlert = () => {
  resetForm()
  closeDropdowns()
  showAlertForm.value = true
}

const openEditAlert = (rule: AlertRule) => {
  editingAlert.value = rule
  formData.value = {
    name: rule.name,
    enabled: rule.enabled,
    eventType: rule.eventType,
    conditionLogic: rule.conditionLogic,
    conditions: rule.conditions.length > 0 ? rule.conditions.map(c => ({ ...c, value: String(c.value) })) : [blankCondition()],
    channels: rule.channels.length > 0 ? rule.channels.map(c => ({ ...c })) : [blankChannel()],
    cooldownMs: rule.cooldownMs,
    sustainedMs: rule.sustainedMs || 0,
    windowCount: rule.windowCount || 0,
    windowMs: rule.windowMs || 0,
    inAppSeverity: rule.inAppSeverity === 'informational' ? 'informational' : 'critical',
  }
  closeDropdowns()
  showAlertForm.value = true
}

const alertApiBase = computed(() => resolveSlackManagerApiBase())

const fetchAlerts = async () => {
  alertsLoading.value = true
  try {
    const [rulesRes, logRes, snoozeRes] = await Promise.all([
      fetch(`${alertApiBase.value}/alerts`),
      fetch(`${alertApiBase.value}/alerts/log`),
      fetch(`${alertApiBase.value}/alerts/snooze-slack`),
    ])
    if (rulesRes.ok) alertRules.value = await rulesRes.json()
    if (logRes.ok) alertLog.value = await logRes.json()
    if (snoozeRes.ok) {
      const s = await snoozeRes.json()
      globalSlackSnoozeUntil.value = s.globalSlackSnoozeUntil ?? null
    }
  } catch (e) {
    console.error('Failed to fetch alerts:', e)
  } finally {
    alertsLoading.value = false
  }
}

const SLACK_SNOOZE_PRESETS = [5, 15, 30, 60, 120] as const

const isGlobalSlackSnoozeActive = computed(() => {
  const u = globalSlackSnoozeUntil.value
  if (!u) return false
  const t = Date.parse(u)
  return !isNaN(t) && t > Date.now()
})

const isRuleSlackSnoozeActive = (rule: AlertRule) => {
  const u = rule.slackSnoozeUntil
  if (!u) return false
  const t = Date.parse(u)
  return !isNaN(t) && t > Date.now()
}

const formatSlackSnoozeUntil = (iso: string) => {
  const t = Date.parse(iso)
  if (isNaN(t)) return iso
  try {
    return new Date(t).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

const snoozeSlackGlobal = async (minutes: number) => {
  try {
    await fetch(`${alertApiBase.value}/alerts/snooze-slack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ minutes }),
    })
    showGlobalSlackSnoozeMenu.value = false
    await fetchAlerts()
  } catch (e) {
    console.error('Snooze Slack (global):', e)
  }
}

const snoozeSlackForRule = async (ruleId: string, minutes: number) => {
  try {
    await fetch(`${alertApiBase.value}/alerts/snooze-slack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ minutes, ruleId }),
    })
    showRuleSlackSnoozeMenuId.value = null
    await fetchAlerts()
  } catch (e) {
    console.error('Snooze Slack (rule):', e)
  }
}

const clearGlobalSlackSnooze = async () => {
  try {
    await fetch(`${alertApiBase.value}/alerts/snooze-slack/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    showGlobalSlackSnoozeMenu.value = false
    await fetchAlerts()
  } catch (e) {
    console.error('Clear global Slack snooze:', e)
  }
}

const clearRuleSlackSnooze = async (ruleId: string) => {
  try {
    await fetch(`${alertApiBase.value}/alerts/snooze-slack/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ruleId }),
    })
    showRuleSlackSnoozeMenuId.value = null
    await fetchAlerts()
  } catch (e) {
    console.error('Clear rule Slack snooze:', e)
  }
}

const saveAlert = async () => {
  const payload = { ...formData.value }
  try {
    if (editingAlert.value) {
      await fetch(`${alertApiBase.value}/alerts/${editingAlert.value.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } else {
      await fetch(`${alertApiBase.value}/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    }
    showAlertForm.value = false
    await fetchAlerts()
  } catch (e) {
    console.error('Failed to save alert:', e)
  }
}

const toggleAlert = async (rule: AlertRule) => {
  try {
    await fetch(`${alertApiBase.value}/alerts/${rule.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !rule.enabled }),
    })
    await fetchAlerts()
  } catch (e) {
    console.error('Failed to toggle alert:', e)
  }
}

const deleteAlert = async (rule: AlertRule) => {
  if (!confirm(`Delete alert "${rule.name}"?`)) return
  try {
    await fetch(`${alertApiBase.value}/alerts/${rule.id}`, { method: 'DELETE' })
    await fetchAlerts()
  } catch (e) {
    console.error('Failed to delete alert:', e)
  }
}

const duplicateAlert = async (rule: AlertRule) => {
  try {
    const payload = {
      name: `${rule.name} (copy)`,
      enabled: rule.enabled,
      eventType: rule.eventType,
      conditionLogic: rule.conditionLogic,
      conditions: rule.conditions.map(c => ({ ...c })),
      channels: rule.channels.map(c => ({ ...c })),
      cooldownMs: rule.cooldownMs,
      sustainedMs: rule.sustainedMs || 0,
      windowCount: rule.windowCount || 0,
      windowMs: rule.windowMs || 0,
    }
    await fetch(`${alertApiBase.value}/alerts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    await fetchAlerts()
  } catch (e) {
    console.error('Failed to duplicate alert:', e)
  }
}

const testAlert = async (rule: AlertRule) => {
  testingAlertId.value = rule.id
  try {
    const res = await fetch(`${alertApiBase.value}/alerts/${rule.id}/test`, { method: 'POST' })
    const data = await res.json()
    if (data.channelResults) {
      const results = data.channelResults.map((r: any) => `${r.channel}: ${r.success ? 'OK' : r.error}`).join('\n')
      alert(`Test results:\n${results}`)
    }
    await fetchAlerts()
  } catch (e) {
    console.error('Failed to test alert:', e)
  } finally {
    testingAlertId.value = null
  }
}

watch(() => props.isVisible, (v) => { if (v) fetchAlerts() }, { immediate: true })

const cooldownOptions = [
  { value: 0, label: 'Realtime (no cooldown)' },
  { value: 10000, label: '10 seconds' },
  { value: 30000, label: '30 seconds' },
  { value: 60000, label: '1 minute' },
  { value: 300000, label: '5 minutes' },
  { value: 600000, label: '10 minutes' },
  { value: 1800000, label: '30 minutes' },
  { value: 3600000, label: '1 hour' },
]

const formatCooldown = (ms: number) => {
  if (ms <= 0) return 'Realtime'
  if (ms < 60000) return `${ms / 1000}s`
  if (ms < 3600000) return `${ms / 60000}m`
  return `${ms / 3600000}h`
}

const sustainedOptions = [
  { value: 0, label: 'Instant (no sustained check)' },
  { value: 3000, label: '3 seconds' },
  { value: 5000, label: '5 seconds' },
  { value: 10000, label: '10 seconds' },
  { value: 15000, label: '15 seconds' },
  { value: 30000, label: '30 seconds' },
  { value: 60000, label: '1 minute' },
  { value: 120000, label: '2 minutes' },
  { value: 300000, label: '5 minutes' },
]

const selectSustained = (val: number) => {
  formData.value.sustainedMs = val
  closeDropdowns()
}

const getSustainedLabel = (v: number) => sustainedOptions.find(o => o.value === v)?.label || `${v / 1000}s`

const formatSustained = (ms: number) => {
  if (ms <= 0) return '—'
  if (ms < 60000) return `${ms / 1000}s`
  if (ms < 3600000) return `${ms / 60000}m`
  return `${ms / 3600000}h`
}

const windowPresets = [
  { count: 0, ms: 0, label: 'Every match (no window)' },
  { count: 3, ms: 60000, label: '3+ matches in 1 minute' },
  { count: 5, ms: 60000, label: '5+ matches in 1 minute' },
  { count: 3, ms: 300000, label: '3+ matches in 5 minutes' },
  { count: 10, ms: 60000, label: '10+ matches in 1 minute' },
]

const formatWindow = (count?: number, ms?: number) => {
  const c = Number(count) || 0
  const w = Number(ms) || 0
  if (c < 2 || w <= 0) return '—'
  const preset = windowPresets.find(p => p.count === c && p.ms === w)
  if (preset && preset.count >= 2) return preset.label.replace(' matches', '')
  return `${c}+ / ${formatCooldown(w)}`
}

const getWindowLabel = (count?: number, ms?: number) => {
  const c = Number(count) || 0
  const w = Number(ms) || 0
  const preset = windowPresets.find(p => p.count === c && p.ms === w)
  if (preset) return preset.label
  if (c < 2 || w <= 0) return 'Every match (no window)'
  return `${c}+ matches in ${formatCooldown(w)}`
}

const selectWindowPreset = (count: number, ms: number) => {
  formData.value.windowCount = count
  formData.value.windowMs = ms
  if (count >= 2 && formData.value.cooldownMs === 0) {
    formData.value.cooldownMs = ms || 60000
  }
  closeDropdowns()
}

const needsValue = (op: ComparisonOp) => op !== 'exists' && op !== 'not_exists'

// --------------- Custom dropdown state for alert form ---------------
const openDropdown = ref<string | null>(null)
const dropdownSearch = ref('')

const toggleDropdown = (id: string) => {
  if (openDropdown.value === id) {
    openDropdown.value = null
    dropdownSearch.value = ''
  } else {
    openDropdown.value = id
    dropdownSearch.value = ''
  }
}

const closeDropdowns = () => {
  openDropdown.value = null
  dropdownSearch.value = ''
  showGlobalSlackSnoozeMenu.value = false
  showRuleSlackSnoozeMenuId.value = null
}

const selectField = (cond: AlertCondition, field: string) => {
  cond.field = field
  closeDropdowns()
}

const selectOperator = (cond: AlertCondition, op: ComparisonOp) => {
  cond.operator = op
  closeDropdowns()
}

const selectEventType = (val: EventType) => {
  const prev = formData.value.eventType
  formData.value.eventType = val
  if ((prev === 'system_stats') !== (val === 'system_stats')) {
    formData.value.conditions = [blankCondition(val)]
    if (val === 'system_stats' && formData.value.cooldownMs < 60000) {
      formData.value.cooldownMs = 60000
    }
  }
  closeDropdowns()
}

const selectChannelType = (ch: AlertChannel, val: AlertChannel['type']) => {
  ch.type = val
  if (val === 'in_app') { ch.url = ''; ch.name = '' }
  else if (val === 'slack' && !ch.url) { ch.url = DEFAULT_SLACK_WEBHOOK; ch.name = '#saas-monitoring' }
  closeDropdowns()
}

const selectCooldown = (val: number) => {
  formData.value.cooldownMs = val
  closeDropdowns()
}

const fieldsForEventType = computed(() => {
  const et = formData.value.eventType
  if (et === 'system_stats') return SYSTEM_STATS_FIELDS
  if (et === 'any') return KNOWN_FIELDS
  return API_FIELDS
})

const filteredFields = computed(() => {
  const base = fieldsForEventType.value
  if (!dropdownSearch.value) return base
  const q = dropdownSearch.value.toLowerCase()
  return base.filter(f => f.includes(q))
})

const filteredOperators = computed(() => {
  if (!dropdownSearch.value) return Object.entries(OPERATOR_LABELS) as [ComparisonOp, string][]
  const q = dropdownSearch.value.toLowerCase()
  return (Object.entries(OPERATOR_LABELS) as [ComparisonOp, string][]).filter(([k, v]) => k.includes(q) || v.includes(q))
})

const EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'api_access', label: 'API Access' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'flow', label: 'Flow' },
  { value: 'system_stats', label: 'System Resources (CPU/RAM)' },
]

const getEventTypeLabel = (v: EventType) => EVENT_TYPE_OPTIONS.find(o => o.value === v)?.label || v
const getChannelTypeLabel = (v: string) => CHANNEL_TYPES.find(o => o.value === v)?.label || v
const getCooldownLabel = (v: number) => cooldownOptions.find(o => o.value === v)?.label || `${v}ms`

const filteredAlertRules = computed(() => {
  const q = alertRulesFilter.value.trim().toLowerCase()
  if (!q) return alertRules.value
  return alertRules.value.filter(rule => {
    const parts = [
      rule.name,
      rule.eventType,
      rule.enabled ? 'enabled on' : 'disabled off',
      ...rule.conditions.map(c => `${c.field} ${c.operator} ${c.value} ${OPERATOR_LABELS[c.operator as ComparisonOp] || ''}`),
      ...rule.channels.map(c => `${c.type} ${c.name || ''} ${c.url || ''}`),
      getCooldownLabel(rule.cooldownMs),
      rule.sustainedMs ? getSustainedLabel(rule.sustainedMs) : '',
      getWindowLabel(rule.windowCount, rule.windowMs),
    ]
    const searchable = parts.filter(Boolean).join(' ').toLowerCase()
    return searchable.includes(q)
  })
})

const ALERT_LOG_GROUP_FIELDS = ['rulename', 'ruleid', 'method', 'status_code', 'path', 'route_action', 'user_email', 'ip', 'fixture_id', 'player_id', 'market', 'duration_ms'] as const

const alertLogFilterParsed = computed(() => {
  const raw = alertLogFilter.value.trim()
  const groupMatch = raw.match(/=group\s*\(\s*([^)]+)\s*\)/i)
  const groupFields: string[] = groupMatch
    ? groupMatch[1].split(',').map(f => f.trim().toLowerCase()).filter(f => (ALERT_LOG_GROUP_FIELDS as readonly string[]).includes(f))
    : []
  const baseFilter = groupMatch ? raw.slice(0, groupMatch.index).trim() : raw
  return { baseFilter, groupFields }
})

const filteredAlertLog = computed(() => {
  const { baseFilter } = alertLogFilterParsed.value
  const q = baseFilter.toLowerCase()
  const byTab = alertLogViewTab.value === 'active'
    ? alertLog.value.filter(e => !archivedAlertIds.value.has(e.id))
    : alertLog.value.filter(e => archivedAlertIds.value.has(e.id))
  if (!q) return byTab
  return byTab.filter(entry => {
    const snap = entry.eventSnapshot || {}
    const parts = [
      entry.ruleName,
      entry.ruleId,
      new Date(entry.firedAt).toLocaleString(),
      snap.method,
      snap.path,
      snap.endpoint_url,
      snap.route_action,
      snap.user_email,
      snap.status_code,
      snap.duration_ms,
      snap.fixture_id,
      snap.player_id,
      snap.market,
      snap.ip,
      snap.bearer_token,
      snap.request_size,
      snap.response_size,
      ...(entry.channelResults || []).map(cr => `${cr.channel} ${cr.success ? 'ok' : 'fail'} ${cr.error || ''}`),
    ]
    const searchable = parts.filter(v => v != null && v !== '').map(String).join(' ').toLowerCase()
    return searchable.includes(q)
  })
})

const getAlertLogFieldValue = (entry: AlertLogEntry, field: string): string => {
  const snap = entry.eventSnapshot || {}
  if (field === 'rulename') return entry.ruleName || '-'
  if (field === 'ruleid') return entry.ruleId || '-'
  if (field === 'path') return String(snap.path || snap.endpoint_url || '-')
  return String(snap[field as keyof typeof snap] ?? '-')
}

const LOG_GROUP_SEP = '\x1F'
const groupedAlertLog = computed(() => {
  const { groupFields } = alertLogFilterParsed.value
  const entries = filteredAlertLog.value
  if (groupFields.length === 0) return null
  const map = new Map<string, AlertLogEntry[]>()
  for (const entry of entries) {
    const parts = groupFields.map(f => getAlertLogFieldValue(entry, f))
    const key = parts.join(LOG_GROUP_SEP)
    const list = map.get(key) || []
    list.push(entry)
    map.set(key, list)
  }
  return Array.from(map.entries()).map(([key, entries]) => ({
    key,
    values: key.split(LOG_GROUP_SEP),
    count: entries.length,
    entries,
  }))
})

const formHasInAppChannel = computed(() => formData.value.channels.some((c) => c.type === 'in_app'))

const onRainChartSelect = (e: AlertLogEntry) => {
  alertLogSelectedEntry.value = e
}

// --------------- Overlay plumbing ---------------
const closeOverlay = () => {
  closeDropdowns()
  showAlertLogColsDropdown.value = false
  alertLogSelectedEntry.value = null
  emit('close')
}

const onDocumentClick = (e: MouseEvent) => {
  const t = e.target as Node
  if (showAlertLogColsDropdown.value && alertLogColsDropdownRef.value && !alertLogColsDropdownRef.value.contains(t)) {
    showAlertLogColsDropdown.value = false
  }
  // Snooze menus and form dropdowns stop propagation on their own clicks, so any click that
  // reaches the document is outside them.
  if (showGlobalSlackSnoozeMenu.value || showRuleSlackSnoozeMenuId.value || openDropdown.value) closeDropdowns()
}

const onKeydown = (e: KeyboardEvent) => {
  if (!props.isVisible || e.key !== 'Escape') return
  if (openDropdown.value || showGlobalSlackSnoozeMenu.value || showRuleSlackSnoozeMenuId.value) { closeDropdowns(); return }
  if (showAlertForm.value) { showAlertForm.value = false; return }
  if (alertLogSelectedEntry.value) { alertLogSelectedEntry.value = null; return }
  closeOverlay()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
  themeObserver = new MutationObserver(() => {
    isDarkMode.value = document.documentElement.classList.contains('dark')
  })
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
  themeObserver?.disconnect()
  themeObserver = null
})
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-2 md:p-4"
    @click.self="closeOverlay"
  >
    <div
      class="w-full max-w-[1500px] h-[92vh] flex flex-col rounded-xl shadow-2xl overflow-hidden text-sm"
      :class="isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-800'"
    >
      <!-- Overlay header -->
      <div class="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
        <div class="flex items-center gap-2 min-w-0">
          <svg class="w-5 h-5 flex-shrink-0" :class="isDarkMode ? 'text-orange-300' : 'text-orange-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
          <h2 class="text-lg font-semibold truncate">Slack Manager</h2>
          <span class="hidden md:inline text-xs truncate" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">alert rules → Slack channels · relay: {{ alertApiBase }}</span>
        </div>
        <button @click="closeOverlay" class="p-2 rounded-lg text-xl leading-none" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'" title="Close (Esc)">✕</button>
      </div>

      <!-- ===== Imported "Alerts" view (vmprod saas-monitoring App.vue) ===== -->
      <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
      <!-- Alerts sub-nav -->
      <div class="flex-shrink-0 px-3 md:px-4 py-2 border-b flex items-center justify-between gap-2" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
        <div class="flex items-center gap-2">
          <span class="text-xs px-2 py-0.5 rounded font-medium" :class="isDarkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'">Alerts</span>
          <div class="flex items-center gap-0 rounded overflow-hidden border" :class="isDarkMode ? 'border-gray-600' : 'border-gray-300'">
            <button @click="alertsTab = 'rules'" class="px-3 py-1 text-xs font-medium transition-colors"
              :class="alertsTab === 'rules' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100')"
            >Rules ({{ alertRules.length }})</button>
            <button @click="alertsTab = 'log'" class="px-3 py-1 text-xs font-medium transition-colors"
              :class="alertsTab === 'log' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100')"
            >Log ({{ alertLog.length }})</button>
            <button @click="alertsTab = 'buckets'" class="px-3 py-1 text-xs font-medium transition-colors"
              :class="alertsTab === 'buckets' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100')"
            >Buckets</button>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- Alert log columns selector -->
          <div v-if="alertsTab === 'log'" ref="alertLogColsDropdownRef" class="relative">
            <button
              @click.stop="showAlertLogColsDropdown = !showAlertLogColsDropdown"
              class="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors"
              :class="showAlertLogColsDropdown
                ? (isDarkMode ? 'bg-orange-600 text-white' : 'bg-orange-500 text-white')
                : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
              Columns
            </button>
            <Transition name="dropdown">
              <div
                v-if="showAlertLogColsDropdown"
                class="absolute right-0 top-full mt-2 z-50 min-w-[200px] rounded-xl shadow-xl border overflow-hidden"
                :class="isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'"
              >
                <div class="px-3 py-2 border-b text-xs font-semibold uppercase tracking-wide" :class="isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'">
                  Toggle columns
                </div>
                <div class="max-h-[320px] overflow-y-auto p-2 space-y-0.5">
                  <label
                    v-for="col in ALERT_LOG_COLUMNS"
                    :key="col.id"
                    class="flex items-center gap-3 px-3 py-1.5 rounded-lg cursor-pointer transition-colors"
                    :class="isDarkMode ? 'hover:bg-gray-700/80' : 'hover:bg-gray-100'"
                  >
                    <input
                      type="checkbox"
                      :checked="isAlertLogColVisible(col.id)"
                      @change="toggleAlertLogCol(col.id)"
                      class="rounded border-gray-400 text-orange-500 focus:ring-orange-500"
                    />
                    <span class="text-xs" :class="isDarkMode ? 'text-gray-200' : 'text-gray-800'">{{ col.label }}</span>
                  </label>
                </div>
                <div class="px-3 py-2 border-t flex justify-between gap-2 text-xs" :class="isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'">
                  <button @click="resetAlertLogCols()" class="hover:underline">Reset</button>
                  <button @click="showAlertLogColsDropdown = false" class="hover:underline">Done</button>
                </div>
              </div>
            </Transition>
          </div>
          <div v-if="alertsTab === 'rules'" class="relative flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              @click.stop="showGlobalSlackSnoozeMenu = !showGlobalSlackSnoozeMenu"
              class="px-2 py-1 text-xs rounded transition-colors flex items-center gap-1"
              :class="isGlobalSlackSnoozeActive
                ? (isDarkMode ? 'bg-purple-900/60 text-purple-200 ring-1 ring-purple-500/50' : 'bg-purple-100 text-purple-800 ring-1 ring-purple-300')
                : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')"
              title="Pause Slack notifications for all rules"
            >
              Slack snooze
              <svg class="w-3 h-3 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
            </button>
            <span
              v-if="isGlobalSlackSnoozeActive && globalSlackSnoozeUntil"
              class="text-[10px] max-w-[140px] truncate"
              :class="isDarkMode ? 'text-purple-300/90' : 'text-purple-700'"
              :title="`Slack muted until ${formatSlackSnoozeUntil(globalSlackSnoozeUntil)}`"
            >until {{ formatSlackSnoozeUntil(globalSlackSnoozeUntil) }}</span>
            <Transition name="dropdown">
              <div
                v-if="showGlobalSlackSnoozeMenu"
                class="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-lg shadow-xl border py-1"
                :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'"
                @click.stop
              >
                <div class="px-2.5 py-1 text-[10px] uppercase tracking-wide" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">All rules · Slack only</div>
                <button
                  v-for="m in SLACK_SNOOZE_PRESETS"
                  :key="`g-${m}`"
                  type="button"
                  class="w-full text-left px-3 py-1.5 text-xs transition-colors"
                  :class="isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-800'"
                  @click="snoozeSlackGlobal(m)"
                >{{ m }} min</button>
                <button
                  v-if="isGlobalSlackSnoozeActive"
                  type="button"
                  class="w-full text-left px-3 py-1.5 text-xs border-t transition-colors"
                  :class="isDarkMode ? 'border-gray-700 text-purple-300 hover:bg-gray-700' : 'border-gray-100 text-purple-700 hover:bg-purple-50'"
                  @click="clearGlobalSlackSnooze()"
                >Clear global snooze</button>
              </div>
            </Transition>
          </div>
          <button @click="fetchAlerts" class="px-2 py-1 text-xs rounded transition-colors" :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'">Refresh</button>
          <button v-if="alertsTab === 'rules'" @click="openNewAlert" class="px-3 py-1 text-xs rounded font-medium transition-colors" :class="isDarkMode ? 'bg-orange-700 hover:bg-orange-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'">+ New Alert</button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="alertsLoading" class="flex-1 flex items-center justify-center">
        <span class="text-sm" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">Loading alerts...</span>
      </div>

      <!-- Rules table -->
      <div v-else-if="alertsTab === 'rules'" class="flex-1 flex flex-col min-h-0 overflow-hidden">
        <!-- Search filter -->
        <div v-if="alertRules.length > 0" class="flex-shrink-0 px-3 py-2 border-b flex items-center gap-2" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
          <svg class="w-4 h-4 flex-shrink-0" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <input v-model="alertRulesFilter" type="text" placeholder="Filter by name, event type, conditions, channels, cooldown..." class="flex-1 min-w-0 px-3 py-1.5 text-sm border rounded"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'">
          <button v-if="alertRulesFilter" @click="alertRulesFilter = ''" class="px-2 py-1 text-xs rounded transition-colors" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'">Clear</button>
        </div>
        <div v-if="alertRules.length === 0" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <div class="text-4xl mb-3 opacity-40">🔔</div>
            <p class="text-sm" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">No alert rules configured</p>
            <button @click="openNewAlert" class="mt-3 px-4 py-2 text-sm rounded font-medium transition-colors" :class="isDarkMode ? 'bg-orange-700 hover:bg-orange-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'">Create your first alert</button>
          </div>
        </div>
        <div v-else-if="filteredAlertRules.length === 0" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <p class="text-sm" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">No rules match your search</p>
            <button @click="alertRulesFilter = ''" class="mt-2 px-3 py-1.5 text-xs rounded transition-colors" :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'">Clear filter</button>
          </div>
        </div>
        <template v-else>
          <!-- Table header -->
          <div class="flex-shrink-0 select-none text-xs font-medium mx-3" :class="isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'">
            <div class="flex items-center">
              <div class="px-3 py-2 w-14 text-center">On</div>
              <div class="px-3 py-2 w-52">Name</div>
              <div class="px-3 py-2 w-24">Event Type</div>
              <div class="px-3 py-2 flex-1">Conditions</div>
              <div class="px-3 py-2 w-44">Channels</div>
              <div class="px-3 py-2 w-20">Cooldown</div>
              <div class="px-3 py-2 w-20">Sustained</div>
              <div class="px-3 py-2 w-28">Window</div>
              <div class="px-3 py-2 w-56 text-right">Actions</div>
            </div>
          </div>
          <!-- Table body -->
          <div class="flex-1 overflow-auto px-3 hide-scrollbar">
            <div v-for="rule in filteredAlertRules" :key="rule.id"
              class="flex items-center border-b text-xs transition-colors"
              :class="[
                isDarkMode ? 'border-gray-800' : 'border-gray-200',
                rule.enabled ? (isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50') : (isDarkMode ? 'opacity-50' : 'opacity-50'),
              ]"
              style="min-height: 40px;"
            >
              <!-- Toggle -->
              <div class="px-3 py-2 w-14 flex justify-center">
                <button @click="toggleAlert(rule)" class="w-9 h-5 rounded-full relative transition-colors flex-shrink-0"
                  :class="rule.enabled ? 'bg-green-500 hover:bg-green-600' : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')">
                  <span class="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200" :class="rule.enabled ? 'translate-x-4' : 'translate-x-0'"></span>
                </button>
              </div>
              <!-- Name -->
              <div class="px-3 py-2 w-52 min-w-0">
                <div class="flex items-center gap-1.5 min-w-0">
                  <span class="font-semibold truncate">{{ rule.name }}</span>
                  <span
                    v-if="isRuleSlackSnoozeActive(rule)"
                    class="flex-shrink-0 px-1 py-0 rounded text-[9px] font-medium"
                    :class="isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'"
                    :title="rule.slackSnoozeUntil ? `Slack snoozed until ${formatSlackSnoozeUntil(rule.slackSnoozeUntil)}` : ''"
                  >Slack</span>
                </div>
                <div
                  class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1 text-[10px] leading-tight"
                  :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'"
                >
                  <span
                    class="tabular-nums font-medium"
                    :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'"
                    title="Times this rule matched and created an alert log entry"
                  >{{ rule.fireStats?.fires ?? 0 }} matches</span>
                  <template v-if="rule.channels?.some(c => c.type === 'in_app') || (rule.fireStats?.inApp ?? 0) > 0">
                    <span class="opacity-50">·</span>
                    <span title="In-app notifications queued for these firings">{{ rule.fireStats?.inApp ?? 0 }} in-app</span>
                    <template v-if="rule.channels?.some(c => c.type === 'in_app')">
                      <span class="opacity-50">·</span>
                      <span
                        :title="rule.inAppSeverity === 'informational' ? 'Routes to activity bell' : 'Routes to critical (red) bell'"
                        :class="rule.inAppSeverity === 'informational'
                          ? (isDarkMode ? 'text-slate-400' : 'text-slate-600')
                          : (isDarkMode ? 'text-red-400' : 'text-red-600')"
                      >{{ rule.inAppSeverity === 'informational' ? 'activity' : 'critical' }}</span>
                    </template>
                  </template>
                  <template v-if="rule.channels?.some(c => c.type !== 'in_app') || (rule.fireStats?.externalOk ?? 0) > 0">
                    <span class="opacity-50">·</span>
                    <span title="Successful Slack, Discord, webhook, or Telegram deliveries (can be &gt;1 per firing)">{{ rule.fireStats?.externalOk ?? 0 }} ext</span>
                  </template>
                </div>
              </div>
              <!-- Event Type -->
              <div class="px-3 py-2 w-24">
                <span class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  :class="isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'">{{ rule.eventType }}</span>
              </div>
              <!-- Conditions -->
              <div class="px-3 py-2 flex-1 min-w-0">
                <div class="flex flex-wrap gap-1">
                  <span v-for="(cond, ci) in rule.conditions" :key="ci" class="px-1.5 py-0.5 rounded font-mono text-[10px]"
                    :class="isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'">
                    {{ cond.field }} {{ OPERATOR_LABELS[cond.operator as ComparisonOp] || cond.operator }} {{ needsValue(cond.operator as ComparisonOp) ? cond.value : '' }}
                  </span>
                  <span v-if="rule.conditions.length > 1" class="px-1 py-0.5 rounded text-[10px] font-bold"
                    :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ rule.conditionLogic.toUpperCase() }}</span>
                </div>
              </div>
              <!-- Channels -->
              <div class="px-3 py-2 w-44">
                <div class="flex flex-wrap gap-1">
                  <span v-for="(ch, chi) in rule.channels" :key="chi" class="px-1.5 py-0.5 rounded text-[10px]"
                    :class="ch.type === 'in_app' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-50 text-orange-700')
                      : ch.type === 'slack' ? (isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700')
                      : ch.type === 'discord' ? (isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700')
                      : ch.type === 'telegram' ? (isDarkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-50 text-sky-700')
                      : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')">
                    {{ ch.type }}{{ ch.name ? ': ' + ch.name : '' }}
                  </span>
                </div>
              </div>
              <!-- Cooldown -->
              <div class="px-3 py-2 w-20">
                <span class="font-mono" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ formatCooldown(rule.cooldownMs) }}</span>
              </div>
              <!-- Sustained -->
              <div class="px-3 py-2 w-20">
                <span class="font-mono" :class="rule.sustainedMs > 0 ? (isDarkMode ? 'text-amber-400' : 'text-amber-600') : (isDarkMode ? 'text-gray-600' : 'text-gray-300')">{{ formatSustained(rule.sustainedMs || 0) }}</span>
              </div>
              <!-- Window -->
              <div class="px-3 py-2 w-28">
                <span class="font-mono" :class="(rule.windowCount || 0) >= 2 && (rule.windowMs || 0) > 0 ? (isDarkMode ? 'text-sky-400' : 'text-sky-600') : (isDarkMode ? 'text-gray-600' : 'text-gray-300')">{{ formatWindow(rule.windowCount, rule.windowMs) }}</span>
              </div>
              <!-- Actions -->
              <div class="px-3 py-2 w-56 flex items-center justify-end gap-0.5 flex-wrap">
                <div class="relative">
                  <button
                    type="button"
                    @click.stop="showRuleSlackSnoozeMenuId = showRuleSlackSnoozeMenuId === rule.id ? null : rule.id"
                    class="px-1.5 py-1 rounded text-[10px] font-medium transition-colors"
                    :class="isRuleSlackSnoozeActive(rule)
                      ? (isDarkMode ? 'bg-purple-900/60 text-purple-200' : 'bg-purple-100 text-purple-800')
                      : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600')"
                    title="Snooze Slack for this rule only"
                  >Slack</button>
                  <Transition name="dropdown">
                    <div
                      v-if="showRuleSlackSnoozeMenuId === rule.id"
                      class="absolute right-0 top-full mt-1 z-50 min-w-[150px] rounded-lg shadow-xl border py-1"
                      :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'"
                      @click.stop
                    >
                      <div class="px-2.5 py-1 text-[10px] uppercase tracking-wide" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">This rule</div>
                      <button
                        v-for="m in SLACK_SNOOZE_PRESETS"
                        :key="`${rule.id}-${m}`"
                        type="button"
                        class="w-full text-left px-3 py-1.5 text-xs transition-colors"
                        :class="isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-800'"
                        @click="snoozeSlackForRule(rule.id, m)"
                      >{{ m }} min</button>
                      <button
                        v-if="isRuleSlackSnoozeActive(rule)"
                        type="button"
                        class="w-full text-left px-3 py-1.5 text-xs border-t transition-colors"
                        :class="isDarkMode ? 'border-gray-700 text-purple-300 hover:bg-gray-700' : 'border-gray-100 text-purple-700 hover:bg-purple-50'"
                        @click="clearRuleSlackSnooze(rule.id)"
                      >Clear rule snooze</button>
                    </div>
                  </Transition>
                </div>
                <button @click="testAlert(rule)" :disabled="testingAlertId === rule.id" class="px-2 py-1 rounded transition-colors"
                  :class="isDarkMode ? 'bg-cyan-900 hover:bg-cyan-800 text-cyan-300 disabled:opacity-50' : 'bg-cyan-100 hover:bg-cyan-200 text-cyan-700 disabled:opacity-50'"
                >{{ testingAlertId === rule.id ? '...' : 'Test' }}</button>
                <button @click="duplicateAlert(rule)" class="px-2 py-1 rounded transition-colors"
                  :class="isDarkMode ? 'bg-violet-900/50 hover:bg-violet-800 text-violet-300' : 'bg-violet-50 hover:bg-violet-100 text-violet-600'"
                  title="Duplicate this rule">Dup</button>
                <button @click="openEditAlert(rule)" class="px-2 py-1 rounded transition-colors"
                  :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'"
                >Edit</button>
                <button @click="deleteAlert(rule)" class="px-2 py-1 rounded transition-colors"
                  :class="isDarkMode ? 'bg-red-900/50 hover:bg-red-800 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'"
                >Del</button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Alert log table -->
      <div v-else-if="alertsTab === 'log'" class="flex-1 flex flex-col min-h-0 overflow-hidden">
        <!-- Log search filter -->
        <div v-if="alertLog.length > 0" class="flex-shrink-0 px-3 py-2 border-b flex items-center gap-2 flex-wrap" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
          <button
            @click="alertLogViewTab = 'active'"
            class="px-2 py-1 rounded text-xs font-medium"
            :class="alertLogViewTab === 'active' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')"
          >Active</button>
          <button
            @click="alertLogViewTab = 'archived'"
            class="px-2 py-1 rounded text-xs font-medium"
            :class="alertLogViewTab === 'archived' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')"
          >Archived ({{ archivedAlertIds.size || 0 }})</button>
          <svg class="w-4 h-4 flex-shrink-0" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <input v-model="alertLogFilter" type="text" placeholder="Filter... Use =group(field,field) e.g. =group(ruleName,status_code) for grouping" class="flex-1 min-w-0 px-3 py-1.5 text-sm border rounded"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'">
          <label class="flex items-center gap-1.5 cursor-pointer select-none">
            <input v-model="showLogMatchUnderlines" type="checkbox" class="rounded border-gray-400 text-orange-500 focus:ring-orange-500">
            <span class="text-xs whitespace-nowrap" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">Rule match</span>
          </label>
          <button v-if="alertLogFilter" @click="alertLogFilter = ''" class="px-2 py-1 text-xs rounded transition-colors" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'">Clear</button>
        </div>
        <div v-if="alertLog.length === 0" class="flex-1 flex items-center justify-center">
          <p class="text-sm" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">No alerts have fired yet</p>
        </div>
        <div v-else-if="filteredAlertLog.length === 0" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <p class="text-sm" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
              {{ alertLogViewTab === 'archived' ? 'No archived entries. Archive rows from Active to see them here.' : 'No log entries match your search' }}
            </p>
            <button v-if="alertLogFilter && alertLogViewTab === 'active'" @click="alertLogFilter = ''" class="mt-2 px-3 py-1.5 text-xs rounded transition-colors" :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'">Clear filter</button>
          </div>
        </div>
        <div v-else class="flex-1 flex overflow-hidden min-h-0 items-stretch">
          <div class="flex-1 flex flex-col min-w-0 overflow-hidden min-h-0">
          <!-- Grouped view when =group(...) is used -->
          <template v-if="groupedAlertLog">
            <div class="flex-shrink-0 select-none text-xs font-medium mx-3" :class="isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'">
              <div class="flex items-center">
                <div class="px-3 py-2 w-8"></div>
                <div v-for="(gf, i) in alertLogFilterParsed.groupFields" :key="gf" class="px-3 py-2 flex-1 min-w-0" :style="i === 0 ? 'min-width: 80px' : ''">{{ gf }}</div>
                <div class="px-3 py-2 w-20 text-right font-bold">Count</div>
                <div class="px-3 py-2 w-20 flex-shrink-0">Actions</div>
              </div>
            </div>
            <div class="flex-1 overflow-auto px-3 hide-scrollbar">
              <div v-for="group in groupedAlertLog" :key="group.key" class="border-b" :class="isDarkMode ? 'border-gray-800' : 'border-gray-200'">
                <div @click="toggleLogGroup(group.key)" class="flex items-center text-xs cursor-pointer transition-colors min-h-[36px]"
                  :class="isDarkMode ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'">
                  <div class="px-3 py-2 w-8">
                    <svg class="w-3.5 h-3.5 transition-transform" :class="expandedAlertLogGroups.has(group.key) ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                  </div>
                  <div v-for="(val, i) in group.values" :key="i" class="px-3 py-2 flex-1 min-w-0 truncate font-mono" :class="isDarkMode ? 'text-gray-300' : 'text-gray-700'" :style="i === 0 ? 'min-width: 80px' : ''">{{ val }}</div>
                  <div class="px-3 py-2 w-20 text-right">
                    <span class="font-bold px-2 py-0.5 rounded" :class="isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'">{{ group.count }}</span>
                  </div>
                </div>
                <div v-if="expandedAlertLogGroups.has(group.key)" class="pl-6" :class="isDarkMode ? 'bg-gray-900/30' : 'bg-gray-50'">
                  <div v-for="entry in group.entries" :key="entry.id" @click.stop="alertLogSelectedEntry = entry" class="flex items-center border-b text-xs py-1.5 cursor-pointer"
                    :class="[isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-100', alertLogSelectedEntry?.id === entry.id ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100') : '']">
                    <div class="px-3 py-1 w-44 text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ new Date(entry.firedAt).toLocaleString() }}</div>
                    <div class="px-3 py-1 flex-1 min-w-0 truncate font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'">{{ entry.eventSnapshot.path || entry.eventSnapshot.endpoint_url || '-' }}</div>
                    <div class="px-3 py-1 w-20 flex-shrink-0" @click.stop>
                      <button v-if="alertLogViewTab === 'active'" @click.stop="archiveAlertEntry(entry)" class="px-1 py-0.5 rounded text-[10px]" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'">Archive</button>
                      <button v-else @click.stop="unarchiveAlertEntry(entry)" class="px-1 py-0.5 rounded text-[10px]" :class="isDarkMode ? 'text-orange-400 hover:bg-gray-700' : 'text-orange-600 hover:bg-gray-200'">Restore</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <!-- Flat table view -->
          <template v-else>
          <!-- Table header -->
          <div class="flex-shrink-0 select-none text-xs font-medium mx-3" :class="isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'">
            <div class="flex items-center">
              <div v-if="isAlertLogColVisible('time')" class="px-3 py-2 w-44">Time</div>
              <div v-if="isAlertLogColVisible('rule')" class="px-3 py-2 w-36">Rule</div>
              <div v-if="isAlertLogColVisible('user_email')" class="px-3 py-2 w-40">User</div>
              <div v-if="isAlertLogColVisible('method')" class="px-3 py-2 w-16">Method</div>
              <div v-if="isAlertLogColVisible('status_code')" class="px-3 py-2 w-20">Status</div>
              <div v-if="isAlertLogColVisible('path')" class="px-3 py-2 flex-1" style="min-width: 160px;">Path</div>
              <div v-if="isAlertLogColVisible('route_action')" class="px-3 py-2 w-44">Action</div>
              <div v-if="isAlertLogColVisible('duration_ms')" class="px-3 py-2 w-20">Duration</div>
              <div v-if="isAlertLogColVisible('fixture_id')" class="px-3 py-2 w-20">Fixture</div>
              <div v-if="isAlertLogColVisible('player_id')" class="px-3 py-2 w-20">Player</div>
              <div v-if="isAlertLogColVisible('market')" class="px-3 py-2 w-28">Market</div>
              <div v-if="isAlertLogColVisible('ip')" class="px-3 py-2 w-32">IP</div>
              <div v-if="isAlertLogColVisible('bearer_token')" class="px-3 py-2 w-36">Token</div>
              <div v-if="isAlertLogColVisible('request_size')" class="px-3 py-2 w-14">Req</div>
              <div v-if="isAlertLogColVisible('response_size')" class="px-3 py-2 w-14">Res</div>
              <div v-if="isAlertLogColVisible('channels')" class="px-3 py-2 w-44">Channels</div>
              <div class="px-3 py-2 w-20 flex-shrink-0">Actions</div>
            </div>
          </div>
          <!-- Table body -->
          <div class="flex-1 overflow-auto px-3 hide-scrollbar">
            <div v-for="entry in filteredAlertLog" :key="entry.id"
              @click="alertLogSelectedEntry = entry"
              class="flex items-center border-b text-xs cursor-pointer"
              :class="[isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-200 hover:bg-gray-50', alertLogSelectedEntry?.id === entry.id ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100') : '']"
              style="min-height: 36px;"
            >
              <div v-if="isAlertLogColVisible('time')" class="px-3 py-2 w-44">
                <span class="font-mono whitespace-nowrap" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ new Date(entry.firedAt).toLocaleString() }}</span>
              </div>
              <div v-if="isAlertLogColVisible('rule')" class="px-3 py-2 w-36">
                <span class="font-semibold truncate block">{{ entry.ruleName }}</span>
              </div>
              <div v-if="isAlertLogColVisible('user_email')" class="px-3 py-2 w-40" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'user_email') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'user_email') && isDarkMode }">
                <span v-if="entry.eventSnapshot.user_email" class="truncate block" :class="isDarkMode ? 'text-purple-400' : 'text-purple-600'">{{ entry.eventSnapshot.user_email }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('method')" class="px-3 py-2 w-16" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'method') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'method') && isDarkMode }">
                <span v-if="entry.eventSnapshot.method" class="font-mono font-bold"
                  :class="entry.eventSnapshot.method === 'GET' ? 'text-green-500' : entry.eventSnapshot.method === 'POST' ? 'text-blue-500' : entry.eventSnapshot.method === 'PUT' ? 'text-orange-500' : entry.eventSnapshot.method === 'DELETE' ? 'text-red-500' : (isDarkMode ? 'text-gray-300' : 'text-gray-600')"
                >{{ entry.eventSnapshot.method }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('status_code')" class="px-3 py-2 w-20" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'status_code') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'status_code') && isDarkMode }">
                <span v-if="entry.eventSnapshot.status_code" class="px-2 py-0.5 rounded font-mono font-bold"
                  :class="Number(entry.eventSnapshot.status_code) >= 500 ? 'bg-red-500 text-white' : Number(entry.eventSnapshot.status_code) >= 400 ? 'bg-yellow-500 text-black' : 'bg-green-500 text-white'"
                >{{ entry.eventSnapshot.status_code }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('path')" class="px-3 py-2 flex-1 min-w-0" style="min-width: 160px;" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'path') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'path') && isDarkMode }">
                <span class="font-mono truncate block" :class="isDarkMode ? 'text-gray-300' : 'text-gray-700'">{{ entry.eventSnapshot.path || entry.eventSnapshot.endpoint_url || '-' }}</span>
              </div>
              <div v-if="isAlertLogColVisible('route_action')" class="px-3 py-2 w-44" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'route_action') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'route_action') && isDarkMode }">
                <span v-if="entry.eventSnapshot.route_action" class="truncate block font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.route_action }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('duration_ms')" class="px-3 py-2 w-20" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'duration_ms') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'duration_ms') && isDarkMode }">
                <span v-if="entry.eventSnapshot.duration_ms" class="font-mono" :class="Number(entry.eventSnapshot.duration_ms) > 1000 ? 'text-red-400' : Number(entry.eventSnapshot.duration_ms) > 300 ? 'text-yellow-400' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')">{{ entry.eventSnapshot.duration_ms }}ms</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('fixture_id')" class="px-3 py-2 w-20" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'fixture_id') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'fixture_id') && isDarkMode }">
                <span v-if="entry.eventSnapshot.fixture_id" class="font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.fixture_id }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('player_id')" class="px-3 py-2 w-20" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'player_id') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'player_id') && isDarkMode }">
                <span v-if="entry.eventSnapshot.player_id" class="font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.player_id }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('market')" class="px-3 py-2 w-28" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'market') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'market') && isDarkMode }">
                <span v-if="entry.eventSnapshot.market" class="truncate block" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.market }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('ip')" class="px-3 py-2 w-32" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'ip') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'ip') && isDarkMode }">
                <span class="font-mono" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ entry.eventSnapshot.ip || '-' }}</span>
              </div>
              <div v-if="isAlertLogColVisible('bearer_token')" class="px-3 py-2 w-36" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'bearer_token') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'bearer_token') && isDarkMode }">
                <span v-if="entry.eventSnapshot.bearer_token" class="font-mono truncate block text-[10px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ String(entry.eventSnapshot.bearer_token).slice(-12) }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('request_size')" class="px-3 py-2 w-14" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'request_size') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'request_size') && isDarkMode }">
                <span v-if="entry.eventSnapshot.request_size" class="font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.request_size }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('response_size')" class="px-3 py-2 w-14" :class="{ 'pulse-cell-match-light': shouldShowLogColUnderline(entry, 'response_size') && !isDarkMode, 'pulse-cell-match-dark': shouldShowLogColUnderline(entry, 'response_size') && isDarkMode }">
                <span v-if="entry.eventSnapshot.response_size" class="font-mono" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">{{ entry.eventSnapshot.response_size }}</span>
                <span v-else :class="isDarkMode ? 'text-gray-600' : 'text-gray-300'">-</span>
              </div>
              <div v-if="isAlertLogColVisible('channels')" class="px-3 py-2 w-44">
                <div class="flex flex-wrap gap-1">
                  <span v-for="(cr, i) in entry.channelResults" :key="i"
                    class="px-1.5 py-0.5 rounded text-[10px]"
                    :class="cr.success
                      ? (isDarkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-50 text-green-700')
                      : (isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-600')"
                  >{{ cr.channel.split(':')[0] }}: {{ cr.success ? 'ok' : cr.error }}</span>
                </div>
              </div>
              <div class="px-3 py-2 w-20 flex-shrink-0">
                <button
                  v-if="alertLogViewTab === 'active'"
                  @click.stop="archiveAlertEntry(entry)"
                  class="px-1.5 py-0.5 rounded text-[10px]"
                  :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'"
                  title="Archive this alert"
                >Archive</button>
                <button
                  v-else
                  @click.stop="unarchiveAlertEntry(entry)"
                  class="px-1.5 py-0.5 rounded text-[10px]"
                  :class="isDarkMode ? 'text-orange-400 hover:bg-gray-700' : 'text-orange-600 hover:bg-gray-200'"
                  title="Restore to Active"
                >Restore</button>
              </div>
            </div>
          </div>
          </template>
          </div>
          <!-- Alert log detail panel -->
          <Transition name="slide">
            <div v-if="alertLogSelectedEntry" class="fixed inset-0 lg:relative lg:inset-auto lg:w-96 flex-shrink-0 lg:border-l overflow-y-auto hide-scrollbar z-40" :class="isDarkMode ? 'bg-gray-800 lg:border-gray-700' : 'bg-white lg:border-gray-200'">
              <div class="sticky top-0 px-4 py-3 border-b flex justify-between items-center" :class="isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
                <h2 class="font-bold text-sm">Details <span class="text-xs font-normal px-1.5 py-0.5 rounded" :class="isDarkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'">Alert</span></h2>
                <button @click="alertLogSelectedEntry = null" class="p-2 rounded-lg" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'">✕</button>
              </div>
              <div class="p-4 space-y-3 text-sm">
                <div class="text-xs font-semibold" :class="isDarkMode ? 'text-orange-300' : 'text-orange-600'">{{ alertLogSelectedEntry.ruleName }}</div>
                <div class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ new Date(alertLogSelectedEntry.firedAt).toLocaleString() }}</div>
                <div class="flex gap-2 items-center">
                  <span v-if="alertLogSelectedEntry.eventSnapshot.method" :class="getMethodColor(String(alertLogSelectedEntry.eventSnapshot.method), isDarkMode)" class="font-bold">{{ alertLogSelectedEntry.eventSnapshot.method }}</span>
                  <span v-if="alertLogSelectedEntry.eventSnapshot.status_code" :class="getStatusColor(Number(alertLogSelectedEntry.eventSnapshot.status_code), isDarkMode)" class="px-2 py-0.5 rounded text-xs font-bold">{{ alertLogSelectedEntry.eventSnapshot.status_code }}</span>
                  <span v-if="alertLogSelectedEntry.eventSnapshot.duration_ms" class="font-mono text-xs" :class="getDurationColor(Number(alertLogSelectedEntry.eventSnapshot.duration_ms), isDarkMode)">{{ alertLogSelectedEntry.eventSnapshot.duration_ms }}ms</span>
                </div>
                <div class="flex gap-4">
                  <div v-if="alertLogSelectedEntry.eventSnapshot.request_size"><span class="text-xs text-gray-500">Request:</span> <span class="text-xs font-mono">{{ formatBytes(Number(alertLogSelectedEntry.eventSnapshot.request_size)) }}</span></div>
                  <div v-if="alertLogSelectedEntry.eventSnapshot.response_size"><span class="text-xs text-gray-500">Response:</span> <span class="text-xs font-mono">{{ formatBytes(Number(alertLogSelectedEntry.eventSnapshot.response_size)) }}</span></div>
                </div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.path || alertLogSelectedEntry.eventSnapshot.endpoint_url"><div class="text-xs text-gray-500 mb-1">PATH</div><div class="font-mono text-xs break-all" :class="isDarkMode ? 'text-gray-300' : 'text-gray-700'">{{ alertLogSelectedEntry.eventSnapshot.path || alertLogSelectedEntry.eventSnapshot.endpoint_url }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.full_url"><div class="text-xs text-gray-500 mb-1">FULL URL</div><div class="font-mono text-xs break-all text-gray-400">{{ alertLogSelectedEntry.eventSnapshot.full_url }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">ACTION</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.route_action || '-' }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">USER</div><div class="text-xs">{{ alertLogSelectedEntry.eventSnapshot.user_email || 'Anonymous' }}<span v-if="alertLogSelectedEntry.eventSnapshot.user_id" class="text-gray-500"> (ID: {{ alertLogSelectedEntry.eventSnapshot.user_id }})</span></div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.bearer_token"><div class="text-xs text-gray-500 mb-1">BEARER TOKEN</div><div class="font-mono text-xs" :class="isDarkMode ? 'text-yellow-400' : 'text-yellow-600'">{{ alertLogSelectedEntry.eventSnapshot.bearer_token }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">IP</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.ip || '-' }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.user_agent"><div class="text-xs text-gray-500 mb-1">USER AGENT</div><div class="text-xs text-gray-400 break-all">{{ alertLogSelectedEntry.eventSnapshot.user_agent }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.fixture_id"><div class="text-xs text-gray-500 mb-1">FIXTURE ID</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.fixture_id }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.player_id"><div class="text-xs text-gray-500 mb-1">PLAYER ID</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.player_id }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.market"><div class="text-xs text-gray-500 mb-1">MARKET</div><div class="text-xs">{{ alertLogSelectedEntry.eventSnapshot.market }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.payload_excerpt">
                  <div class="text-xs text-gray-500 mb-1">REQUEST BODY</div>
                  <pre class="text-xs p-2 rounded overflow-x-auto max-h-48" :class="isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100'">{{ formatJson(typeof alertLogSelectedEntry.eventSnapshot.payload_excerpt === 'string' ? alertLogSelectedEntry.eventSnapshot.payload_excerpt : JSON.stringify(alertLogSelectedEntry.eventSnapshot.payload_excerpt)) || alertLogSelectedEntry.eventSnapshot.payload_excerpt }}</pre>
                </div>
                <div v-else class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"><span class="text-gray-500">REQUEST BODY</span> — not captured</div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.response_excerpt">
                  <div class="text-xs text-gray-500 mb-1">RESPONSE BODY</div>
                  <pre class="text-xs p-2 rounded overflow-x-auto max-h-48" :class="isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100'">{{ formatJson(typeof alertLogSelectedEntry.eventSnapshot.response_excerpt === 'string' ? alertLogSelectedEntry.eventSnapshot.response_excerpt : JSON.stringify(alertLogSelectedEntry.eventSnapshot.response_excerpt)) || alertLogSelectedEntry.eventSnapshot.response_excerpt }}</pre>
                </div>
                <div v-else class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"><span class="text-gray-500">RESPONSE BODY</span> — not captured</div>
                <div v-if="alertLogSelectedEntry.channelResults?.length"><div class="text-xs text-gray-500 mb-1">CHANNELS</div><div class="space-y-1"><div v-for="(cr, i) in alertLogSelectedEntry.channelResults" :key="i" class="text-xs" :class="cr.success ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')">{{ cr.channel }}: {{ cr.success ? 'ok' : (cr.error || 'fail') }}</div></div></div>
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Alert buckets: one column per rule, drops stack bottom-up -->
      <div v-else-if="alertsTab === 'buckets'" class="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div v-if="alertRules.length > 0" class="flex-shrink-0 px-3 py-2 border-b flex items-center gap-2 flex-wrap" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
          <button
            @click="alertLogViewTab = 'active'"
            class="px-2 py-1 rounded text-xs font-medium"
            :class="alertLogViewTab === 'active' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')"
          >Active</button>
          <button
            @click="alertLogViewTab = 'archived'"
            class="px-2 py-1 rounded text-xs font-medium"
            :class="alertLogViewTab === 'archived' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')"
          >Archived ({{ archivedAlertIds.size || 0 }})</button>
          <svg class="w-4 h-4 flex-shrink-0" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <input v-model="alertLogFilter" type="text" placeholder="Filter log entries for buckets…" class="flex-1 min-w-0 px-3 py-1.5 text-sm border rounded"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'">
          <button v-if="alertLogFilter" @click="alertLogFilter = ''" class="px-2 py-1 text-xs rounded transition-colors" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'">Clear</button>
          <span class="text-[10px] max-w-[min(280px,40vw)] leading-snug" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">Uses the same Active / Archived filter and search as Log. Each column is a rule; drops rain in and stack at the bottom.</span>
        </div>
        <div v-if="alertRules.length === 0" class="flex-1 flex items-center justify-center px-4">
          <p class="text-sm text-center" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">Add alert rules on the Rules tab to see buckets here.</p>
        </div>
        <div v-else-if="filteredAlertRules.length === 0" class="flex-1 flex items-center justify-center px-4">
          <div class="text-center">
            <p class="text-sm" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">No rules match your Rules filter. Open Rules or clear the filter there.</p>
          </div>
        </div>
        <div v-else class="flex-1 flex overflow-hidden min-h-0 items-stretch">
          <div class="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
            <AlertLogRainChart
              :entries="filteredAlertLog"
              :all-rules="filteredAlertRules.map(r => ({ id: r.id, name: r.name }))"
              layout="full"
              title="Buckets"
              :is-dark-mode="isDarkMode"
              :selected-id="alertLogSelectedEntry?.id ?? null"
              :animate-drop-id="rainDropAnimateId"
              @select="onRainChartSelect"
            />
          </div>
          <Transition name="slide">
            <div v-if="alertLogSelectedEntry" class="fixed inset-0 lg:relative lg:inset-auto lg:w-96 flex-shrink-0 lg:border-l overflow-y-auto hide-scrollbar z-40" :class="isDarkMode ? 'bg-gray-800 lg:border-gray-700' : 'bg-white lg:border-gray-200'">
              <div class="sticky top-0 px-4 py-3 border-b flex justify-between items-center" :class="isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
                <h2 class="font-bold text-sm">Details <span class="text-xs font-normal px-1.5 py-0.5 rounded" :class="isDarkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'">Alert</span></h2>
                <button @click="alertLogSelectedEntry = null" class="p-2 rounded-lg" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'">✕</button>
              </div>
              <div class="p-4 space-y-3 text-sm">
                <div class="text-xs font-semibold" :class="isDarkMode ? 'text-orange-300' : 'text-orange-600'">{{ alertLogSelectedEntry.ruleName }}</div>
                <div class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">{{ new Date(alertLogSelectedEntry.firedAt).toLocaleString() }}</div>
                <div class="flex gap-2 items-center">
                  <span v-if="alertLogSelectedEntry.eventSnapshot.method" :class="getMethodColor(String(alertLogSelectedEntry.eventSnapshot.method), isDarkMode)" class="font-bold">{{ alertLogSelectedEntry.eventSnapshot.method }}</span>
                  <span v-if="alertLogSelectedEntry.eventSnapshot.status_code" :class="getStatusColor(Number(alertLogSelectedEntry.eventSnapshot.status_code), isDarkMode)" class="px-2 py-0.5 rounded text-xs font-bold">{{ alertLogSelectedEntry.eventSnapshot.status_code }}</span>
                  <span v-if="alertLogSelectedEntry.eventSnapshot.duration_ms" class="font-mono text-xs" :class="getDurationColor(Number(alertLogSelectedEntry.eventSnapshot.duration_ms), isDarkMode)">{{ alertLogSelectedEntry.eventSnapshot.duration_ms }}ms</span>
                </div>
                <div class="flex gap-4">
                  <div v-if="alertLogSelectedEntry.eventSnapshot.request_size"><span class="text-xs text-gray-500">Request:</span> <span class="text-xs font-mono">{{ formatBytes(Number(alertLogSelectedEntry.eventSnapshot.request_size)) }}</span></div>
                  <div v-if="alertLogSelectedEntry.eventSnapshot.response_size"><span class="text-xs text-gray-500">Response:</span> <span class="text-xs font-mono">{{ formatBytes(Number(alertLogSelectedEntry.eventSnapshot.response_size)) }}</span></div>
                </div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.path || alertLogSelectedEntry.eventSnapshot.endpoint_url"><div class="text-xs text-gray-500 mb-1">PATH</div><div class="font-mono text-xs break-all" :class="isDarkMode ? 'text-gray-300' : 'text-gray-700'">{{ alertLogSelectedEntry.eventSnapshot.path || alertLogSelectedEntry.eventSnapshot.endpoint_url }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.full_url"><div class="text-xs text-gray-500 mb-1">FULL URL</div><div class="font-mono text-xs break-all text-gray-400">{{ alertLogSelectedEntry.eventSnapshot.full_url }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">ACTION</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.route_action || '-' }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">USER</div><div class="text-xs">{{ alertLogSelectedEntry.eventSnapshot.user_email || 'Anonymous' }}<span v-if="alertLogSelectedEntry.eventSnapshot.user_id" class="text-gray-500"> (ID: {{ alertLogSelectedEntry.eventSnapshot.user_id }})</span></div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.bearer_token"><div class="text-xs text-gray-500 mb-1">BEARER TOKEN</div><div class="font-mono text-xs" :class="isDarkMode ? 'text-yellow-400' : 'text-yellow-600'">{{ alertLogSelectedEntry.eventSnapshot.bearer_token }}</div></div>
                <div><div class="text-xs text-gray-500 mb-1">IP</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.ip || '-' }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.user_agent"><div class="text-xs text-gray-500 mb-1">USER AGENT</div><div class="text-xs text-gray-400 break-all">{{ alertLogSelectedEntry.eventSnapshot.user_agent }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.fixture_id"><div class="text-xs text-gray-500 mb-1">FIXTURE ID</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.fixture_id }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.player_id"><div class="text-xs text-gray-500 mb-1">PLAYER ID</div><div class="font-mono text-xs">{{ alertLogSelectedEntry.eventSnapshot.player_id }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.market"><div class="text-xs text-gray-500 mb-1">MARKET</div><div class="text-xs">{{ alertLogSelectedEntry.eventSnapshot.market }}</div></div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.payload_excerpt">
                  <div class="text-xs text-gray-500 mb-1">REQUEST BODY</div>
                  <pre class="text-xs p-2 rounded overflow-x-auto max-h-48" :class="isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100'">{{ formatJson(typeof alertLogSelectedEntry.eventSnapshot.payload_excerpt === 'string' ? alertLogSelectedEntry.eventSnapshot.payload_excerpt : JSON.stringify(alertLogSelectedEntry.eventSnapshot.payload_excerpt)) || alertLogSelectedEntry.eventSnapshot.payload_excerpt }}</pre>
                </div>
                <div v-else class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"><span class="text-gray-500">REQUEST BODY</span> — not captured</div>
                <div v-if="alertLogSelectedEntry.eventSnapshot.response_excerpt">
                  <div class="text-xs text-gray-500 mb-1">RESPONSE BODY</div>
                  <pre class="text-xs p-2 rounded overflow-x-auto max-h-48" :class="isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100'">{{ formatJson(typeof alertLogSelectedEntry.eventSnapshot.response_excerpt === 'string' ? alertLogSelectedEntry.eventSnapshot.response_excerpt : JSON.stringify(alertLogSelectedEntry.eventSnapshot.response_excerpt)) || alertLogSelectedEntry.eventSnapshot.response_excerpt }}</pre>
                </div>
                <div v-else class="text-xs" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"><span class="text-gray-500">RESPONSE BODY</span> — not captured</div>
                <div v-if="alertLogSelectedEntry.channelResults?.length"><div class="text-xs text-gray-500 mb-1">CHANNELS</div><div class="space-y-1"><div v-for="(cr, i) in alertLogSelectedEntry.channelResults" :key="i" class="text-xs" :class="cr.success ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')">{{ cr.channel }}: {{ cr.success ? 'ok' : (cr.error || 'fail') }}</div></div></div>
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Create/Edit Alert Modal -->
      <Teleport to="body">
        <Transition name="fade">
          <div v-if="showAlertForm" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="showAlertForm = false">
            <div class="absolute inset-0 bg-black/70" @click="showAlertForm = false"></div>
            <div class="relative w-full max-w-5xl max-h-[95vh] flex flex-col rounded-lg shadow-2xl overflow-hidden" :class="isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'">
              <!-- Header -->
              <div class="flex items-center justify-between px-6 py-4 border-b" :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'">
                <h3 class="font-bold text-lg" :class="isDarkMode ? '' : 'text-gray-800'">{{ editingAlert ? 'Edit Alert Rule' : 'New Alert Rule' }}</h3>
                <button @click="showAlertForm = false" class="text-2xl leading-none px-2 hover:opacity-70" :class="isDarkMode ? 'text-gray-400' : 'text-gray-400 hover:text-gray-600'">&times;</button>
              </div>
              <!-- Body -->
              <div class="flex-1 overflow-auto p-6 space-y-6" @click.self="closeDropdowns">
                <!-- Name & Event Type -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1.5" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Alert Name</label>
                    <input v-model="formData.name" type="text" placeholder="e.g. 500 errors" class="w-full px-3 py-2.5 text-sm border rounded"
                      :class="isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 placeholder-gray-400'" @focus="closeDropdowns">
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1.5" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Event Type</label>
                    <div class="relative">
                      <button @click.stop="toggleDropdown('eventType')" class="w-full flex items-center justify-between px-3 py-2 text-sm border rounded transition-colors"
                        :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                          openDropdown === 'eventType' ? (isDarkMode ? 'border-orange-500 ring-1 ring-orange-500/30' : 'border-orange-400 ring-1 ring-orange-400/30') : '']">
                        <span>{{ getEventTypeLabel(formData.eventType) }}</span>
                        <svg class="w-3.5 h-3.5 transition-transform" :class="openDropdown === 'eventType' ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="openDropdown === 'eventType'" class="absolute z-20 left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden"
                          :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'">
                          <div v-for="opt in EVENT_TYPE_OPTIONS" :key="opt.value"
                            @click.stop="selectEventType(opt.value)"
                            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors"
                            :class="[formData.eventType === opt.value ? (isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="formData.eventType === opt.value ? 'bg-orange-500' : 'bg-transparent'"></span>
                            {{ opt.label }}
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </div>

                <!-- Conditions -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="text-sm font-medium" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Conditions</label>
                    <div class="flex items-center gap-2">
                      <!-- Condition logic toggle -->
                      <div class="flex items-center gap-0 rounded-md overflow-hidden border" :class="isDarkMode ? 'border-gray-600' : 'border-gray-200'">
                        <button @click="formData.conditionLogic = 'and'" class="px-2.5 py-1 text-xs font-medium transition-colors"
                          :class="formData.conditionLogic === 'and' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-50 text-gray-500 hover:bg-gray-100')">AND</button>
                        <button @click="formData.conditionLogic = 'or'" class="px-2.5 py-1 text-xs font-medium transition-colors"
                          :class="formData.conditionLogic === 'or' ? (isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white') : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-50 text-gray-500 hover:bg-gray-100')">OR</button>
                      </div>
                      <button @click="formData.conditions.push(blankCondition(formData.eventType))" class="text-xs px-2 py-1 rounded transition-colors"
                        :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'">+ Add</button>
                    </div>
                  </div>
                  <div class="space-y-2">
                    <div v-for="(cond, ci) in formData.conditions" :key="ci" class="flex items-center gap-2 group">
                      <!-- Field dropdown -->
                      <div class="relative flex-1 min-w-0">
                        <button @click.stop="toggleDropdown(`field-${ci}`)" class="w-full flex items-center justify-between gap-1 px-2 py-1.5 text-xs border rounded font-mono transition-colors truncate"
                          :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                            openDropdown === `field-${ci}` ? (isDarkMode ? 'border-blue-500 ring-1 ring-blue-500/30' : 'border-blue-400 ring-1 ring-blue-400/30') : '']">
                          <span class="truncate">{{ cond.field }}</span>
                          <svg class="w-3 h-3 flex-shrink-0 transition-transform" :class="openDropdown === `field-${ci}` ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                        </button>
                        <Transition name="dropdown">
                          <div v-if="openDropdown === `field-${ci}`" class="absolute z-20 left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden"
                            :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'" style="min-width: 180px">
                            <div class="p-1.5 border-b" :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'">
                              <input v-model="dropdownSearch" type="text" placeholder="Search fields..." class="w-full px-2 py-1 text-xs border rounded font-mono"
                                :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-100 placeholder-gray-400'"
                                @click.stop>
                            </div>
                            <div class="max-h-48 overflow-auto">
                              <div v-for="f in filteredFields" :key="f"
                                @click.stop="selectField(cond, f)"
                                class="flex items-center gap-2 px-3 py-1.5 text-xs font-mono cursor-pointer transition-colors"
                                :class="[cond.field === f ? (isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="cond.field === f ? 'bg-blue-500' : 'bg-transparent'"></span>
                                {{ f }}
                              </div>
                              <div v-if="filteredFields.length === 0" class="px-3 py-2 text-xs text-center" :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'">No matches</div>
                            </div>
                          </div>
                        </Transition>
                      </div>
                      <!-- Operator dropdown -->
                      <div class="relative w-28 flex-shrink-0">
                        <button @click.stop="toggleDropdown(`op-${ci}`)" class="w-full flex items-center justify-between gap-1 px-2 py-1.5 text-xs border rounded transition-colors"
                          :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                            openDropdown === `op-${ci}` ? (isDarkMode ? 'border-purple-500 ring-1 ring-purple-500/30' : 'border-purple-400 ring-1 ring-purple-400/30') : '']">
                          <span>{{ OPERATOR_LABELS[cond.operator] }}</span>
                          <svg class="w-3 h-3 flex-shrink-0 transition-transform" :class="openDropdown === `op-${ci}` ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                        </button>
                        <Transition name="dropdown">
                          <div v-if="openDropdown === `op-${ci}`" class="absolute z-20 left-0 mt-1 rounded-lg border shadow-xl overflow-hidden"
                            :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'" style="min-width: 150px">
                            <div class="p-1.5 border-b" :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'">
                              <input v-model="dropdownSearch" type="text" placeholder="Search..." class="w-full px-2 py-1 text-xs border rounded"
                                :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-100 placeholder-gray-400'"
                                @click.stop>
                            </div>
                            <div class="max-h-48 overflow-auto">
                              <div v-for="[op, label] in filteredOperators" :key="op"
                                @click.stop="selectOperator(cond, op)"
                                class="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer transition-colors"
                                :class="[cond.operator === op ? (isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="cond.operator === op ? 'bg-purple-500' : 'bg-transparent'"></span>
                                <span class="font-mono">{{ label }}</span>
                                <span class="ml-auto text-[10px] opacity-50">{{ op }}</span>
                              </div>
                              <div v-if="filteredOperators.length === 0" class="px-3 py-2 text-xs text-center" :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'">No matches</div>
                            </div>
                          </div>
                        </Transition>
                      </div>
                      <!-- Value input -->
                      <input v-if="needsValue(cond.operator)" v-model="cond.value" type="text" placeholder="value" class="flex-1 min-w-0 px-2 py-1.5 text-xs border rounded font-mono"
                        :class="isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 placeholder-gray-400'" @focus="closeDropdowns">
                      <span v-else class="flex-1"></span>
                      <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button @click.stop="closeDropdowns(); formData.conditions.splice(ci + 1, 0, { ...cond })" title="Duplicate condition"
                          class="w-6 h-6 flex items-center justify-center rounded-full text-xs transition-colors"
                          :class="isDarkMode ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                        </button>
                        <button v-if="formData.conditions.length > 1" @click.stop="formData.conditions.splice(ci, 1)" title="Remove condition"
                          class="w-6 h-6 flex items-center justify-center rounded-full text-xs transition-colors"
                          :class="isDarkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-400'">&times;</button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Channels -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="text-sm font-medium" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Notification Channels</label>
                    <button @click="formData.channels.push(blankChannel())" class="text-xs px-2 py-1 rounded transition-colors"
                      :class="isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'">+ Add Channel</button>
                  </div>
                  <div class="space-y-2">
                    <div v-for="(ch, chi) in formData.channels" :key="chi" class="flex items-center gap-2 group">
                      <!-- Channel type dropdown -->
                      <div class="relative w-36 flex-shrink-0">
                        <button @click.stop="toggleDropdown(`ch-${chi}`)" class="w-full flex items-center justify-between gap-1 px-2 py-1.5 text-xs border rounded transition-colors"
                          :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                            openDropdown === `ch-${chi}` ? (isDarkMode ? 'border-green-500 ring-1 ring-green-500/30' : 'border-green-400 ring-1 ring-green-400/30') : '']">
                          <span>{{ getChannelTypeLabel(ch.type) }}</span>
                          <svg class="w-3 h-3 flex-shrink-0 transition-transform" :class="openDropdown === `ch-${chi}` ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                        </button>
                        <Transition name="dropdown">
                          <div v-if="openDropdown === `ch-${chi}`" class="absolute z-20 left-0 mt-1 rounded-lg border shadow-xl overflow-hidden"
                            :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'" style="min-width: 170px">
                            <div v-for="ct in CHANNEL_TYPES" :key="ct.value"
                              @click.stop="selectChannelType(ch, ct.value)"
                              class="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors"
                              :class="[ch.type === ct.value ? (isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                              <span class="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-[10px]"
                                :class="ct.value === 'in_app' ? (isDarkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-600')
                                  : ct.value === 'slack' ? (isDarkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600')
                                  : ct.value === 'discord' ? (isDarkMode ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600')
                                  : ct.value === 'telegram' ? (isDarkMode ? 'bg-sky-900/50 text-sky-400' : 'bg-sky-100 text-sky-600')
                                  : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500')">
                                <span v-if="ct.value === 'in_app'">🔔</span>
                                <span v-else-if="ct.value === 'slack'">#</span>
                                <span v-else-if="ct.value === 'discord'">D</span>
                                <span v-else-if="ct.value === 'telegram'">T</span>
                                <span v-else>⚡</span>
                              </span>
                              <span>{{ ct.label }}</span>
                            </div>
                          </div>
                        </Transition>
                      </div>
                      <input v-model="ch.name" type="text" :placeholder="ch.type === 'slack' ? '#channel or C0…' : 'Label (optional)'" class="w-28 px-2 py-1.5 text-xs border rounded"
                        :class="isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 placeholder-gray-400'" @focus="closeDropdowns">
                      <input v-if="ch.type !== 'in_app'" v-model="ch.url" type="text" placeholder="Webhook URL" class="flex-1 min-w-0 px-2 py-1.5 text-xs border rounded font-mono"
                        :class="isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 placeholder-gray-400'" @focus="closeDropdowns">
                      <span v-else class="flex-1 text-xs px-2 py-1.5 italic" :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'">In-app: choose bell type below (critical vs activity)</span>
                      <button v-if="formData.channels.length > 1" @click="formData.channels.splice(chi, 1)"
                        class="w-6 h-6 flex items-center justify-center rounded-full text-xs transition-colors opacity-0 group-hover:opacity-100"
                        :class="isDarkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-400'">&times;</button>
                    </div>
                  </div>
                </div>

                <div v-if="formHasInAppChannel" class="rounded-lg border px-3 py-2.5"
                  :class="isDarkMode ? 'border-gray-600 bg-gray-800/50' : 'border-gray-200 bg-gray-50/80'">
                  <label class="block text-sm font-medium mb-2" :class="isDarkMode ? 'text-gray-300' : 'text-gray-600'">In-app bell</label>
                  <div class="flex flex-wrap gap-2">
                    <button type="button" @click="formData.inAppSeverity = 'critical'"
                      class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
                      :class="formData.inAppSeverity === 'critical'
                        ? (isDarkMode ? 'border-red-500 bg-red-950/40 text-red-300' : 'border-red-400 bg-red-50 text-red-700')
                        : (isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-100')">
                      Critical — red bell
                    </button>
                    <button type="button" @click="formData.inAppSeverity = 'informational'"
                      class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
                      :class="formData.inAppSeverity === 'informational'
                        ? (isDarkMode ? 'border-slate-500 bg-slate-900/50 text-slate-200' : 'border-slate-400 bg-slate-100 text-slate-700')
                        : (isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-100')">
                      Informational — activity bell
                    </button>
                  </div>
                  <p class="text-[11px] mt-2 leading-snug" :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'">
                    Critical is styled like a 500-style alert (red bell, warning toast). Informational keeps the neutral header bell and calmer toast.
                  </p>
                </div>

                <!-- Cooldown, Sustained & Enabled -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1.5" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Cooldown (min interval)</label>
                    <div class="relative">
                      <button @click.stop="toggleDropdown('cooldown')" class="w-full flex items-center justify-between px-3 py-2 text-sm border rounded transition-colors"
                        :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                          openDropdown === 'cooldown' ? (isDarkMode ? 'border-orange-500 ring-1 ring-orange-500/30' : 'border-orange-400 ring-1 ring-orange-400/30') : '']">
                        <span>{{ getCooldownLabel(formData.cooldownMs) }}</span>
                        <svg class="w-3.5 h-3.5 transition-transform" :class="openDropdown === 'cooldown' ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="openDropdown === 'cooldown'" class="absolute z-20 left-0 right-0 bottom-full mb-1 rounded-lg border shadow-xl overflow-hidden"
                          :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'">
                          <div v-for="opt in cooldownOptions" :key="opt.value"
                            @click.stop="selectCooldown(opt.value)"
                            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors"
                            :class="[formData.cooldownMs === opt.value ? (isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="formData.cooldownMs === opt.value ? 'bg-orange-500' : 'bg-transparent'"></span>
                            {{ opt.label }}
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1.5" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Sustained duration</label>
                    <div class="relative">
                      <button @click.stop="toggleDropdown('sustained')" class="w-full flex items-center justify-between px-3 py-2 text-sm border rounded transition-colors"
                        :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                          openDropdown === 'sustained' ? (isDarkMode ? 'border-amber-500 ring-1 ring-amber-500/30' : 'border-amber-400 ring-1 ring-amber-400/30') : '']">
                        <span>{{ getSustainedLabel(formData.sustainedMs) }}</span>
                        <svg class="w-3.5 h-3.5 transition-transform" :class="openDropdown === 'sustained' ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="openDropdown === 'sustained'" class="absolute z-20 left-0 right-0 bottom-full mb-1 rounded-lg border shadow-xl overflow-hidden"
                          :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'">
                          <div v-for="opt in sustainedOptions" :key="opt.value"
                            @click.stop="selectSustained(opt.value)"
                            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors"
                            :class="[formData.sustainedMs === opt.value ? (isDarkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="formData.sustainedMs === opt.value ? 'bg-amber-500' : 'bg-transparent'"></span>
                            {{ opt.label }}
                          </div>
                        </div>
                      </Transition>
                    </div>
                    <p class="mt-1 text-[10px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">Alert only after condition is continuously true for this duration</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1.5" :class="isDarkMode ? 'text-gray-300' : 'text-gray-500'">Rate window</label>
                    <div class="relative">
                      <button @click.stop="toggleDropdown('window')" class="w-full flex items-center justify-between px-3 py-2 text-sm border rounded transition-colors"
                        :class="[isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
                          openDropdown === 'window' ? (isDarkMode ? 'border-sky-500 ring-1 ring-sky-500/30' : 'border-sky-400 ring-1 ring-sky-400/30') : '']">
                        <span>{{ getWindowLabel(formData.windowCount, formData.windowMs) }}</span>
                        <svg class="w-3.5 h-3.5 transition-transform" :class="openDropdown === 'window' ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="openDropdown === 'window'" class="absolute z-20 left-0 right-0 bottom-full mb-1 rounded-lg border shadow-xl overflow-hidden"
                          :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'">
                          <div v-for="opt in windowPresets" :key="opt.count + ':' + opt.ms"
                            @click.stop="selectWindowPreset(opt.count, opt.ms)"
                            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors"
                            :class="[formData.windowCount === opt.count && formData.windowMs === opt.ms ? (isDarkMode ? 'bg-sky-900/30 text-sky-300' : 'bg-sky-50 text-sky-700') : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50')]">
                            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="formData.windowCount === opt.count && formData.windowMs === opt.ms ? 'bg-sky-500' : 'bg-transparent'"></span>
                            {{ opt.label }}
                          </div>
                        </div>
                      </Transition>
                    </div>
                    <p class="mt-1 text-[10px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">Fire only after N matching statuses in a sliding window. Overrides sustained when set.</p>
                  </div>
                  <div class="flex items-end pb-1">
                    <label class="flex items-center gap-3 cursor-pointer select-none group/toggle">
                      <button @click="formData.enabled = !formData.enabled" class="w-10 h-5.5 rounded-full relative transition-colors flex-shrink-0"
                        :class="formData.enabled ? 'bg-green-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-200')">
                        <span class="absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform" :class="formData.enabled ? 'translate-x-5' : 'translate-x-0.5'"></span>
                      </button>
                      <span class="text-sm" :class="formData.enabled ? '' : (isDarkMode ? 'text-gray-500' : 'text-gray-500')">{{ formData.enabled ? 'Enabled' : 'Disabled' }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <!-- Footer -->
              <div class="flex items-center justify-end gap-3 px-6 py-4 border-t" :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'">
                <button @click="showAlertForm = false" class="px-3 py-1.5 rounded text-sm" :class="isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'">Cancel</button>
                <button @click="saveAlert" class="px-4 py-1.5 rounded text-sm font-medium transition-colors" :class="isDarkMode ? 'bg-orange-700 hover:bg-orange-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'">
                  {{ editingAlert ? 'Update' : 'Create' }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Transitions + utilities the imported view relies on (from saas-monitoring App.vue <style>) */
@media (min-width: 1024px) {
  .slide-enter-active, .slide-leave-active { transition: transform 0.2s ease-out; }
  .slide-enter-from, .slide-leave-to { transform: translateX(100%); }
}
@media (max-width: 1023px) {
  .slide-enter-active, .slide-leave-active { transition: opacity 0.15s ease-out; }
  .slide-enter-from, .slide-leave-to { opacity: 0; }
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.dropdown-enter-active, .dropdown-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(-6px); }
.hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
.hide-scrollbar::-webkit-scrollbar { display: none; }
.pulse-cell-match-light { animation: pulse-cell-match-light 2s ease-in-out infinite; }
@keyframes pulse-cell-match-light {
  0%, 100% { background-color: rgb(255 237 213 / 0.7); }
  50% { background-color: rgb(255 237 213 / 0.25); }
}
.pulse-cell-match-dark { animation: pulse-cell-match-dark 2s ease-in-out infinite; }
@keyframes pulse-cell-match-dark {
  0%, 100% { background-color: rgb(154 52 18 / 0.45); }
  50% { background-color: rgb(154 52 18 / 0.15); }
}
</style>
